import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { api, formatINR } from "@/lib/api";
import { toast } from "sonner";
import AddressBook from "@/components/AddressBook";

export default function Checkout() {
  const { cart, refresh } = useCart() || { cart: { items: [] } };
  const { user, updateProfile } = useAuth();
  const nav = useNavigate();
  const [address, setAddress] = useState({ label: "Home", line1: "", line2: "", city: "", state: "", pincode: "", phone: "" });
  const [selectedAddrId, setSelectedAddrId] = useState(null);
  const [contact, setContact] = useState({ name: "", email: "" });
  const [method, setMethod] = useState("razorpay");
  const [code, setCode] = useState("");

  useEffect(() => {
    if (!user) return;
    setContact({ name: user.name || "", email: user.email || "" });
  }, [user]);

  const pickAddress = (a) => { setSelectedAddrId(a.id); setAddress({ label: a.label, line1: a.line1, line2: a.line2, city: a.city, state: a.state, pincode: a.pincode, phone: a.phone }); };

  if (!user) { nav("/auth"); return null; }

  const place = async () => {
    if (!address.line1 || !address.city || !address.pincode) return toast.error("Please choose or add a delivery address");
    try {
      const { data } = await api.post("/checkout", { address, coupon_code: code || undefined, payment_method: method });
      if (contact.name && contact.name !== user.name) { try { await updateProfile({ name: contact.name, email: contact.email }); } catch {} }
      toast.success(`Order ${data.id} placed (${method === "cod" ? "Cash on Delivery" : "Paid"}).`);
      await refresh();
      nav(`/order-confirmed/${data.id}`, { replace: true });
    } catch (e) { toast.error(e?.response?.data?.detail || "Checkout failed"); }
  };

  return (
    <main className="sq-container py-12" data-testid="checkout-page">
      <h1 className="font-display text-4xl md:text-5xl">Checkout</h1>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12 mt-10">
        <div className="lg:col-span-2 space-y-8">
          <section>
            <div className="sq-eyebrow">Your Details</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-4">
              {[["name", "Full Name"], ["email", "Email"]].map(([k, l]) => (
                <label key={k} className="block"><span className="sq-eyebrow">{l}</span>
                  <input data-testid={`contact-${k}`} value={contact[k]} onChange={(e) => setContact({ ...contact, [k]: e.target.value })} className="w-full bg-transparent border-b border-black/30 focus:border-squire-jeans outline-none mt-2 pb-2 text-sm" />
                </label>
              ))}
            </div>
          </section>
          <section>
            <div className="sq-eyebrow mb-4">Delivery Address</div>
            <AddressBook selectedId={selectedAddrId} onSelect={pickAddress} />
          </section>
          <section>
            <div className="sq-eyebrow">Payment Method</div>
            <div className="mt-4 grid grid-cols-2 gap-3 max-w-md">
              {["razorpay", "cod"].map((m) => (
                <button key={m} data-testid={`pay-${m}`} onClick={() => setMethod(m)} className={`p-4 border text-left rounded-lg ${method === m ? "border-squire-jeans bg-[#F5F5F5]" : "border-black/20"}`}>
                  <div className="font-display text-xl">{m === "razorpay" ? "Pay Online" : "Cash on Delivery"}</div>
                  <div className="text-xs text-[#4B5563] mt-1">{m === "razorpay" ? "UPI / Card / Netbanking" : "Pay in cash on receipt"}</div>
                </button>
              ))}
            </div>
          </section>
        </div>
        <aside className="border border-black/10 rounded-xl p-6 h-fit">
          <div className="sq-eyebrow">Order Summary</div>
          <div className="mt-4 max-h-60 overflow-y-auto sq-scroll space-y-2 text-sm">
            {cart.items.map((it, i) => (
              <div key={i} className="flex justify-between"><span>{it.product.name} × {it.quantity}</span><span>{formatINR(it.line_total)}</span></div>
            ))}
          </div>
          <div className="mt-6 flex gap-2">
            <input data-testid="checkout-coupon" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="COUPON" className="flex-1 bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
          </div>
          <div className="mt-6 space-y-1 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span>{formatINR(cart.subtotal)}</span></div>
            <div className="flex justify-between"><span>Tax</span><span>{formatINR(cart.tax)}</span></div>
            <div className="flex justify-between font-semibold pt-3 border-t border-black/10"><span>Total</span><span>{formatINR(cart.total)}</span></div>
          </div>
          <button data-testid="checkout-place" onClick={place} className="sq-btn-primary w-full mt-6">Place Order</button>
        </aside>
      </div>
    </main>
  );
}
